import Button from '../../../../components/Button';
import {
  useCreateFundsBulk,
  useDeleteFundsBulk,
  useGetFundsByPeriodId
} from '../../../../services/funds.service';
import { Wrapper } from './style';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useCopyJurisdictions,
  useGetAutomationFundMappings
} from '../../../../services/automation.service';
import { WarningIcon, PlusIcon } from './icons';

import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FundRow } from './components/FundRow';
import { useGetWorkflowById } from '../../../../services/workflow.services';
import { ProjectContainer } from '../../style';
import { NewAIFDrawer } from '../components/NewAIFDrawer';
import { useUserGroupsCurrentUserAdminState } from '../../../../services/user.service';
import { permissionPolicies } from '../../../../permissions/policies';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import agentStore from '../../../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../../../components/ChatWidget/constants';
import { REPORT_NAMINGS, REPORT_TYPES } from '../../../../consts/reportTypes';
import { useWorkflowChrome } from '../../../../components/Layout/WorkflowChromeContext';
import { EditableProjectName } from '../../../../components/EditableProjectName';

export default function AddAIFs() {
  const navigate = useNavigate();
  const { periodId, id } = useParams();

  const [searchParams] = useSearchParams();

  const [isMutating, setIsMutating] = useState(false);
  const [currentFunds, setCurrentFunds] = useState([]);
  const [previousFunds, setPreviousFunds] = useState([]);
  const [isOpenAddFundModal, setIsOpenAddFundModal] = useState(false);

  const copyJurisdictions = useCopyJurisdictions();
  const { data: quarters, isLoading } = useGetWorkflowById({
    id,
    queryParams: {
      enabled: !!id
    }
  });

  const withJurisdiction = Boolean(searchParams.get('with_jurisdiction'));
  const currentPeriod = quarters?.periods?.find(el => el.id === periodId);
  const previousPeriod = quarters?.periods?.find(el => el.id === currentPeriod?.previous_period_id);
  // Backend stores scratch as 'sratch' (typo preserved by API contract).
  // Scratch periods have no `previous_period_id` and aren't represented in
  // the automation fund-mappings endpoint — we fetch funds directly via
  // useGetFundsByPeriodId instead, and hide the "Previous Period" column.
  const isScratch = currentPeriod?.template_type === 'sratch';

  // Populate the unified header's project/period chips + stepper. AIFs is
  // the active stage on this page.
  const { setLabels, clearLabels, setToolbarSlots } = useWorkflowChrome();
  useEffect(() => {
    setLabels({
      projectLabel: quarters?.name,
      periodLabel:
        currentPeriod?.year && currentPeriod?.period
          ? `${currentPeriod.year}, ${currentPeriod.period}`
          : undefined,
      workflowId: id,
      periodId,
      activeStageKey: 'aifs',
      // Editable project name lives in the toolbar's left slot. The
      // EditableProjectName component renders the name as plain text for
      // non-scratch periods and exposes a pencil-to-edit affordance only
      // for `template_type === 'sratch'`.
      toolbarLeftContent: <EditableProjectName workflowId={id} period={currentPeriod} />
    });
    return () => clearLabels();
  }, [
    quarters?.name,
    currentPeriod?.year,
    currentPeriod?.period,
    currentPeriod?.template_type,
    id,
    periodId,
    setLabels,
    clearLabels
  ]);

  // Automation: fetch fund mappings (current vs previous period comparison).
  // Gate off for scratch — the endpoint isn't meaningful without a previous
  // period, and scratch sources funds via useGetFundsByPeriodId below.
  const fundMappings = useGetAutomationFundMappings({
    periodId,
    queryParams: {
      enabled: !!periodId && !isScratch,
      onSuccess: res => {
        setCurrentFunds([
          ...(res?.mapped_input_funds || []).map(fund => ({
            ...fund,
            isDeleted: false
          })),
          ...(res?.not_mapped_current_input_funds || []).map(fund => ({
            ...fund,
            isDeleted: false
          }))
        ]);

        setPreviousFunds([
          ...(res?.not_mapped_previus_input_funds || []).map(fund => ({
            previous_fund: { ...fund },
            current_fund: {},
            isDeleted: false
          }))
        ]);

        return res;
      }
    }
  });

  // Scratch: fetch funds directly. Shape them like the automation
  // `mapped_input_funds` entries so the existing render + save flow works
  // unchanged (id, name, isDeleted).
  useGetFundsByPeriodId({
    periodId,
    queryParams: {
      enabled: !!periodId && isScratch,
      onSuccess: res => {
        setCurrentFunds(
          (res || []).map(fund => ({
            id: fund.id,
            name: fund.name,
            isDeleted: false
          }))
        );
        // No previous period for scratch.
        setPreviousFunds([]);
        return res;
      }
    }
  });

  const toggleAddFundModal = () => {
    setIsOpenAddFundModal(prev => !prev);
  };

  const handleCreateFunds = names => {
    setCurrentFunds(prev => [
      ...prev,
      ...names.map(name => ({
        name,
        isDeleted: false,
        id: null
      }))
    ]);

    toggleAddFundModal();
  };

  const handleCopy = () => {
    copyJurisdictions.mutate(
      { period_id: periodId },
      {
        onSuccess: () => {
          navigate(`/project/quaters/${id}/${periodId}/jurisdictions`);
        },
        onError: () => {
          toast.error('Error on copying jurisdictions from previous period', {
            position: 'bottom-center'
          });
        }
      }
    );
  };

  const handleDelete = (fundId, index) => {
    if (fundId)
      setCurrentFunds(prev => [
        ...prev.map(el => (el.id === fundId ? { ...el, isDeleted: true } : el))
      ]);
    else
      setCurrentFunds(prev => [
        ...prev.map((el, idx) => (idx === index - 1 ? { ...el, isDeleted: true } : el))
      ]);
  };

  const handleRecover = (fundId, index) => {
    if (fundId)
      setCurrentFunds(prev => [
        ...prev.map(el => (el.id === fundId ? { ...el, isDeleted: false } : el))
      ]);
    else
      setCurrentFunds(prev => [
        ...prev.map((el, idx) => (idx === index - 1 ? { ...el, isDeleted: false } : el))
      ]);
  };

  const createFundBulk = useCreateFundsBulk();
  const deleteFundBulk = useDeleteFundsBulk();

  const { data: currentUser } = useUserGroupsCurrentUserAdminState();

  const mappedInputFundsLength =
    fundMappings?.data?.mapped_input_funds?.length +
    fundMappings?.data?.not_mapped_previus_input_funds?.length;

  console.log(currentUser, quarters);

  const isConversionReportType = quarters?.report_type === REPORT_TYPES.CONVERSION;
  const canManageWorkflow =
    !isConversionReportType &&
    (currentUser && quarters
      ? permissionPolicies.checkAccess({
          currentUserRole: currentUser?.role,
          currentUserId: currentUser?.azure_object_id,
          assignedTo: quarters?.assigned_to
        })
      : true);
  const onSave = async () => {
    const newFunds = currentFunds.filter(el => !el.id && !el.isDeleted).map(el => el.name);

    const deletedFunds = currentFunds
      .filter(el => el.id && el.isDeleted)
      .map(el => el?.current_fund?.id || el?.id);

    const requestArray = [];

    if (newFunds.length > 0)
      requestArray.push(
        createFundBulk.mutateAsync({
          periodId,
          data: { names: newFunds }
        })
      );

    if (deletedFunds.length > 0)
      requestArray.push(
        deleteFundBulk.mutateAsync({
          data: {
            fund_id_list: deletedFunds
          }
        })
      );

    setIsMutating(true);
    Promise.all(requestArray)
      .then(() => {
        const selectedPeriod = quarters?.periods?.find(el => el.id === periodId);

        toast.success(
          `${REPORT_NAMINGS.FUND[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}s have been added successful`,
          {
            position: 'bottom-center'
          }
        );

        if (withJurisdiction && !selectedPeriod?.jurisdiction_count) {
          return handleCopy();
        }

        // Stay on the AIFs page after a successful save. The previous
        // redirect to the Quarters dashboard interrupted the workflow —
        // users would lose their place and have to click back into the
        // period. The toast above already confirms the save succeeded.
      })
      .catch(() => {
        toast.error('Error on saving data', {
          position: 'bottom-center'
        });
      })
      .finally(() => {
        setIsMutating(false);
      });
  };

  useEffect(() => {
    agentStore.setCurrentScreen(WEB_PAGE_STATE_ENUMS.AutomationInputFund);
  }, []);

  // The Save / Next action lives in the unified workflow toolbar (the white
  // bar that sits under the stepper). We register it via `setToolbarSlots`
  // and use a ref so the slot effect doesn't re-fire on every render — the
  // onSave closure recomputes with each state change.
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  });

  useEffect(() => {
    if (!canManageWorkflow) {
      setToolbarSlots(null);
      return undefined;
    }
    setToolbarSlots({
      saveAction: {
        label: isMutating ? 'Saving...' : withJurisdiction ? 'Next' : 'Save',
        onClick: () => onSaveRef.current?.(),
        disabled: isMutating,
        loading: isMutating,
        variant: 'primary'
      }
    });
    return () => setToolbarSlots(null);
  }, [canManageWorkflow, isMutating, withJurisdiction, setToolbarSlots]);

  return (
    <>
      <Wrapper>
        <ProjectContainer width="900px">
          <div className="box">
            <div className="header">
              <div className="warning-text">
                <WarningIcon />
                <p>
                  {isScratch
                    ? `Add the ${REPORT_NAMINGS.FUND[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}s for this period.`
                    : `Compare the required ${REPORT_NAMINGS.FUND[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}s with the previous period and add the missing ones if necessary.`}
                </p>
              </div>
              <PermissionGuard allowed={canManageWorkflow}>
                <Button
                  onClick={toggleAddFundModal}
                  variant="outlined"
                  size="xs"
                  leftIcon={<PlusIcon />}
                >
                  Add New {REPORT_NAMINGS.FUND[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}
                </Button>
              </PermissionGuard>
            </div>
            <div className="table">
              <div className="table-header">
                <div className="th">
                  <div className="left-element">
                    <p
                      style={{
                        color: '#868E96'
                      }}
                    >
                      #
                    </p>
                  </div>
                </div>

                {!isScratch && (
                  <div className="th">
                    <div className="left-element">
                      <p>Previous Period:</p>
                      <p>
                        {previousPeriod?.period}, {previousPeriod?.year}
                      </p>
                    </div>
                    <div className="right-element">
                      <p className="aif-amount">
                        {mappedInputFundsLength}{' '}
                        {REPORT_NAMINGS.FUND[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}s
                      </p>
                    </div>
                  </div>
                )}

                <div className="th">
                  <div className="left-element">
                    <p>Current Period:</p>
                    {currentPeriod?.period}, {currentPeriod?.year}
                  </div>
                  <div className="right-element">
                    <p className="aif-amount">
                      {currentFunds?.filter(el => !el?.isDeleted)?.length}{' '}
                      {REPORT_NAMINGS.FUND[quarters?.report_type || REPORT_TYPES.ANNEX_IV]}s
                    </p>
                  </div>
                </div>
              </div>
              <div className="table-body">
                {currentFunds?.map((item, idx) => (
                  <FundRow
                    index={idx + 1}
                    item={item}
                    key={item.id || idx}
                    isDeleted={item.isDeleted}
                    showDelete={canManageWorkflow}
                    handleDelete={handleDelete}
                    handleRecover={handleRecover}
                  />
                ))}
                {previousFunds?.map((item, idx) => (
                  <FundRow
                    index={idx + 1 + (currentFunds?.length || 0)}
                    item={item}
                    key={item.id}
                  />
                ))}
              </div>
            </div>
          </div>
        </ProjectContainer>
      </Wrapper>

      <NewAIFDrawer
        key={isOpenAddFundModal}
        isOpen={isOpenAddFundModal}
        onClose={toggleAddFundModal}
        isLoading={createFundBulk.isLoading}
        onSubmit={handleCreateFunds}
        hideAIFM={true}
      />
    </>
  );
}
